<template>
  <card-dialog
    v-model="dialog"
    :title="$t('media_library.share_settings.title')"
    max-width="560px"
  >
    <div class="pa-4">
      <v-switch
        v-model="form.isShared"
        :label="$t('switch.share_across_organisation')"
        :hint="$t('media_library.share_across_organisation.hint')"
        persistent-hint
        inset
        dense
        class="mt-0 mb-4"
        @change="onSharedChange"
      />

      <template v-if="form.isShared">
        <v-divider class="mb-4" />

        <v-select
          v-model="form.teamIds"
          :items="teams.data"
          :loading="teams.loading"
          item-value="id"
          item-text="name"
          :label="$t('media_library.share_with_teams')"
          :hint="$t('media_library.share_with_teams.hint')"
          persistent-hint
          multiple
          chips
          small-chips
          deletable-chips
          outlined
          dense
          class="mb-4"
          @change="onTeamsChange"
        />

        <v-switch
          v-model="form.playerVisibility"
          :label="$t('visible_by_player.info')"
          :disabled="form.teamIds.length === 0"
          :hint="form.teamIds.length === 0 ? $t('media_library.visible_to_players.requires_team') : undefined"
          :persistent-hint="form.teamIds.length === 0"
          inset
          dense
          class="mt-0 mb-4"
        />

        <v-select
          v-if="form.playerVisibility && form.teamIds.length > 0"
          v-model="form.playerIds"
          :items="players.data"
          :loading="players.loading"
          item-value="id"
          :item-text="(p) => `${p.last_name}, ${p.first_name}`"
          :label="$t('media_library.share_with_players')"
          :hint="$t('media_library.share_with_players.hint')"
          persistent-hint
          multiple
          chips
          small-chips
          deletable-chips
          outlined
          dense
        />
      </template>
    </div>

    <template v-slot:actions>
      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn
          text
          class="text-none"
          @click="dialog = false"
        >
          {{ $t('button.cancel') }}
        </v-btn>
        <v-btn
          color="primary"
          :loading="saving"
          @click="save()"
        >
          {{ $t('training_builder.button.save') }}
        </v-btn>
      </v-card-actions>
    </template>
  </card-dialog>
</template>

<script>
import CardDialog from '@/components/helpers/CardDialog.vue'
import { TeamApiService } from '@/data/services/TeamApiService'
import { PlayerApiService } from '@/data/services/PlayerApiService'
import { MediaApiService } from '@/data/services/MediaApiService'

export default {
  name: 'MediaShareSettingsDialog',
  components: { CardDialog },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    media: {
      type: Object,
      default: null
    }
  },
  data: () => ({
    teams: {
      loading: false,
      data: []
    },
    players: {
      loading: false,
      data: []
    },
    form: {
      isShared: false,
      teamIds: [],
      playerVisibility: false,
      playerIds: []
    },
    saving: false
  }),
  computed: {
    dialog: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      }
    }
  },
  watch: {
    value(value) {
      if (value) this.init()
    }
  },
  methods: {
    async init() {
      this.form.isShared = !!(this.media && this.media.is_shared)
      this.form.teamIds = (this.media && this.media.teams) ? this.media.teams.map(t => t.id) : []
      this.form.playerVisibility = !!(this.media && this.media.player_visibility)
      this.form.playerIds = (this.media && this.media.player_ids) ? [...this.media.player_ids] : []

      await this.fetchTeams()

      if (this.form.teamIds.length > 0) await this.fetchPlayers()
    },
    async fetchTeams() {
      if (this.teams.data.length > 0) return

      this.teams.loading = true

      try {
        const result = await TeamApiService.getAll()
        this.teams.data = result.data
      } catch (e) {

      } finally {
        this.teams.loading = false
      }
    },
    async fetchPlayers() {
      if (this.form.teamIds.length === 0) {
        this.players.data = []
        return
      }

      this.players.loading = true

      try {
        const result = await PlayerApiService.getSimpleList({ team_ids: this.form.teamIds })
        this.players.data = result.data

        // Drop any selected players that no longer belong to the selected teams.
        const ids = this.players.data.map(p => p.id)
        this.form.playerIds = this.form.playerIds.filter(id => ids.includes(id))
      } catch (e) {

      } finally {
        this.players.loading = false
      }
    },
    onSharedChange() {
      if (!this.form.isShared) {
        this.form.teamIds = []
        this.form.playerVisibility = false
        this.form.playerIds = []
        this.players.data = []
      }
    },
    async onTeamsChange() {
      if (this.form.teamIds.length === 0) {
        this.form.playerVisibility = false
        this.form.playerIds = []
        this.players.data = []
        return
      }

      await this.fetchPlayers()
    },
    async save() {
      if (!this.media) return

      this.saving = true

      try {
        const response = await MediaApiService.update(this.media.uuid, {
          is_shared: this.form.isShared,
          team_ids: this.form.isShared ? this.form.teamIds : [],
          player_visibility: this.form.isShared ? this.form.playerVisibility : false,
          player_ids: (this.form.isShared && this.form.playerVisibility) ? this.form.playerIds : []
        })

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.media_share_settings_saved.success'),
          color: 'success'
        })

        this.$emit('updated', response.data)

        this.dialog = false
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.media_share_settings_saved.error'),
          color: 'error'
        })
      } finally {
        this.saving = false
      }
    }
  }
}
</script>
